import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { HoverHintContent } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  CLAMPQC_HUB_WORK_ORDERS_QUERY_KEY,
  clampQcWorkOrderStatusHoverHintText,
  clampQcReportingRollupRowsFromList,
  clampQcWorkOrderStatusChipClassName,
  clampQcWorkOrderStatusChipLabel,
  fetchClampQcHubWorkOrders,
  type ClampQcReportingRollupRow,
} from "./clampQcBuildOrdersApi";
import { ManufacturingLocationChip, manufacturingLocationShortLabel } from "@/lib/manufacturingLocationChip";
import { ClampQcReportingWorkOrderCell } from "./clampQcReportingUi";

function formatDelta(value: number | null): string {
  if (value == null) {
    return "—";
  }
  return value.toFixed(2);
}

function clampQcReportingRemaining(plannedQuantity: number, quantityComplete: number): number {
  return Math.max(0, plannedQuantity - quantityComplete);
}

type JobStatusFilter = "all" | ClampQcReportingRollupRow["status"];

export function ClampQcReportingPage() {
  const workOrdersQuery = useQuery({
    queryKey: CLAMPQC_HUB_WORK_ORDERS_QUERY_KEY,
    queryFn: fetchClampQcHubWorkOrders,
    staleTime: 30_000,
  });

  const allRows = useMemo(
    () => clampQcReportingRollupRowsFromList(workOrdersQuery.data ?? []),
    [workOrdersQuery.data],
  );

  const [statusFilter, setStatusFilter] = useState<JobStatusFilter>("all");
  const [search, setSearch] = useState("");

  const filteredRows = useMemo(() => {
    let rows = allRows;
    if (statusFilter !== "all") {
      rows = rows.filter((row) => row.status === statusFilter);
    }
    const needle = search.trim().toLowerCase();
    if (needle) {
      rows = rows.filter((row) => {
        const haystack = [
          row.workOrder,
          row.sku,
          row.locationCode,
          manufacturingLocationShortLabel(row.locationId, row.locationCode),
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(needle);
      });
    }
    return rows;
  }, [allRows, statusFilter, search]);

  const kpi = useMemo(() => {
    return {
      workOrderCount: filteredRows.length,
      plannedQuantitySum: filteredRows.reduce((accumulator, row) => accumulator + row.plannedQuantity, 0),
      quantityCompleteSum: filteredRows.reduce((accumulator, row) => accumulator + row.quantityComplete, 0),
      failCountSum: filteredRows.reduce((accumulator, row) => accumulator + row.failCount, 0),
    };
  }, [filteredRows]);

  const loadError =
    workOrdersQuery.isError && workOrdersQuery.error instanceof Error ? workOrdersQuery.error.message : null;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-xl font-semibold text-slate-900">Clamp QC reporting</h1>
        <p className="text-sm text-slate-600">
          Roll-up by QC work order from hub-persisted inspections. Use <span className="font-medium">All lines</span> on
          a row for per-serial drill-down.
        </p>
      </header>

      {loadError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          Could not load work orders: {loadError}
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Job status</span>
            {(["all", "active", "on_hold", "complete"] satisfies JobStatusFilter[]).map((status) => (
              <Button
                key={status}
                size="sm"
                variant={statusFilter === status ? "primary" : "secondary"}
                type="button"
                onClick={() => setStatusFilter(status)}
              >
                {status === "all"
                  ? "All"
                  : status === "active"
                    ? "Active"
                    : status === "on_hold"
                      ? "On hold"
                      : "Complete"}
              </Button>
            ))}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">Search jobs</label>
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Work order, SKU, or location…"
              className="mt-1 max-w-xl"
              autoComplete="off"
            />
          </div>
        </CardBody>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Work orders (filtered)</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-2xl font-semibold tabular-nums text-slate-900">{kpi.workOrderCount}</p>
          </CardBody>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Planned quantity (sum)</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-2xl font-semibold tabular-nums text-slate-900">
              {kpi.plannedQuantitySum.toLocaleString()}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Quantity complete (sum)</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-2xl font-semibold tabular-nums text-slate-900">
              {kpi.quantityCompleteSum.toLocaleString()}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Fails (sum)</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-2xl font-semibold tabular-nums text-slate-900">{kpi.failCountSum}</p>
          </CardBody>
        </Card>
      </div>

      <Card className="min-w-0">
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            {workOrdersQuery.isLoading ? (
              <>
                <Loader2 aria-hidden className="h-4 w-4 animate-spin text-slate-500" />
                <span>Loading work orders…</span>
              </>
            ) : filteredRows.length === allRows.length ? (
              `${allRows.length} work orders`
            ) : (
              `${filteredRows.length} of ${allRows.length} work orders`
            )}
          </CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto overflow-y-hidden p-0 [-webkit-overflow-scrolling:touch]">
          <table className="min-w-[960px] w-full border-collapse text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2">Work order</th>
                <th className="px-3 py-2">SKU</th>
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right tabular-nums">Planned qty</th>
                <th className="px-3 py-2 text-right tabular-nums">Quantity complete</th>
                <th className="px-3 py-2 text-right tabular-nums">Remaining</th>
                <th className="px-3 py-2 text-right tabular-nums">Fails</th>
                <th className="px-3 py-2 text-right tabular-nums">Avg delta</th>
                <th className="px-3 py-2 text-right">Lines</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredRows.map((row) => (
                <tr key={row.slug} className="bg-white">
                  <td className="px-3 py-2">
                    <ClampQcReportingWorkOrderCell row={row} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-800">{row.sku}</td>
                  <td className="px-3 py-2">
                    {row.locationId != null ? (
                      <ManufacturingLocationChip
                        locationId={row.locationId}
                        fallbackLabel={row.locationCode}
                      />
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    <HoverHintContent
                      hoverHint={clampQcWorkOrderStatusHoverHintText({
                        hubStatus: row.hubStatus,
                        executionMode: row.executionMode,
                        mvStatus: row.mvStatus,
                      })}
                      align="center"
                    >
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ring-1",
                          clampQcWorkOrderStatusChipClassName(row.status, row.hubStatus),
                        )}
                      >
                        {clampQcWorkOrderStatusChipLabel(row.status, row.hubStatus)}
                      </span>
                    </HoverHintContent>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.plannedQuantity.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.quantityComplete.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {clampQcReportingRemaining(row.plannedQuantity, row.quantityComplete).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.failCount}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatDelta(row.avgLineHarvestDelta)}</td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/clamp-qc/reporting/jobs/${encodeURIComponent(row.slug)}`}
                      className="font-medium text-brand-700 hover:text-brand-800"
                    >
                      All lines
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!workOrdersQuery.isLoading && filteredRows.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-slate-600">No jobs match these filters.</p>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
